import { Dropdown } from '@/components/Dropdown/Dropdown';
import { RawDataMenuItem, shouldShowRawMenuItem } from '@/components/Dropdown/MenuItems/RawDataMenuItem';
import { RenameMenuItem } from '@/components/Dropdown/MenuItems/RenameMenuItem';
import { ThreeDotsCircle } from '@/components/ThreeDotsCircle';
import { EditableTitle } from '@/components/Title/EditableTitle';
import { toast } from '@/components/toast';
import { useUpdateAvatarName } from '@/pages/avatar';
import { avatarToDeleteAtom } from '@/pages/avatar/ListPage/components';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import { logger } from '@/services/logger';
import { isDvpPresenter, isPublicPresenter, Presenter } from '@d-id/types';
import styled from '@emotion/styled';
import { Divider, MenuList, ListItemIcon as MuiListItemIcon, MenuItem as MuiMenuItem, Typography } from '@mui/material';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import { DeleteMenuItem } from './Options';

const StyledList = styled(MenuList)`
    padding: 0;

    &:focus {
        outline: none;
    }
`;

export const ListItemIcon = styled(MuiListItemIcon)`
    min-width: 3.2rem;
`;

const StyledDivider = styled(Divider)`
    margin: 1rem 0;
`;

const TitleContainer = styled(MuiMenuItem)`
    border-radius: 8px;
    padding: 6px;

    &:hover,
    &:focus {
        background-color: #fff;
    }
`;

const AvatarName = styled(EditableTitle)`
    font-weight: 700;
    font-size: 20px;
    letter-spacing: 0.05rem;
    overflow: hidden;

    &:focus,
    &:hover {
        border: none;
    }

    &:not(:focus) {
        text-overflow: ellipsis;
    }
`;

const CreationDate = styled('p')`
    font-size: 12px;
    color: ${({ theme }) => theme.colors.black[600]};
    margin-top: 4px;
    margin-left: 8px;
`;

const OptionsButtonWrapper = styled.div`
    position: absolute;
    right: 16px;
    top: 16px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    z-index: 2;
`;

const dateFormatOptions: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };

export const ListItemText = ({ children, className }: { children: ReactNode; className?: string }) => {
    return (
        <Typography variant="h5" fontWeight={600} className={className}>
            {children}
        </Typography>
    );
};

interface Props {
    avatar: Presenter;
    onOpen?: () => void;
    onClose?: () => void;
    editable: boolean;
}

export const AvatarOptionsList = ({ avatar, onOpen, onClose, editable }: Props) => {
    const mixpanel = useAnalytics();

    const titleRef = useRef<HTMLDivElement | null>(null);
    const [avatarName, setAvatarName] = useState(avatar.name);
    const [closed, setClosed] = useState<boolean>(true);
    const setAvatarToDelete = useSetRecoilState(avatarToDeleteAtom);
    const { mutateAsync: updateAvatarName } = useUpdateAvatarName();
    const { user } = useAuth();

    useEffect(() => {
        if (avatar.name) {
            setAvatarName(avatar.name);
        }
    }, [avatar.name]);

    const updateName = async (value: string) => {
        mixpanel?.track('avatar-name', { event: 'click', value, from: 'avatar-menu' });

        try {
            await updateAvatarName({ avatar, name: value });
        } catch (e) {
            toast.error({ title: 'Failed to update avatar name, please try again' });
            logger.error('Failed to update avatar name', { e });
        }
    };

    const handleCloseMenu = () => {
        setClosed(true);
        onClose?.();
    };

    const handleOpenMenu = () => {
        setClosed(false);
        onOpen?.();
    };

    const items = [
        ...(editable ? [{ label: <RenameMenuItem titleRef={titleRef} /> }] : []),
        { label: <StyledDivider /> },
        ...((isDvpPresenter(avatar) && isPublicPresenter(avatar)) || !editable
            ? []
            : [{ label: <DeleteMenuItem avatar={avatar} closeMenu={handleCloseMenu} onDelete={() => setAvatarToDelete(avatar)} /> }]),
    ];

    if (shouldShowRawMenuItem(user)) {
        items.push({
            label: <RawDataMenuItem dataToShow={avatar} closeMenu={handleCloseMenu} />,
        });
    }

    return (
        <Dropdown
            closed={closed}
            onOpen={handleOpenMenu}
            onClose={handleCloseMenu}
            trigger={
                <OptionsButtonWrapper>
                    <ThreeDotsCircle data-testid="avatar-options-three-dots-circle" isActive={!closed} />
                </OptionsButtonWrapper>
            }
            items={items}
            renderMenuWrapper={children => (
                <StyledList>
                    <TitleContainer
                        onKeyDown={e => {
                            e.stopPropagation();
                        }}>
                        <AvatarName ref={titleRef} fontSize={20} value={avatarName} onChange={setAvatarName} onBlur={updateName} />
                    </TitleContainer>
                    <CreationDate>Created {new Date(avatar.created_at).toLocaleDateString('en-US', dateFormatOptions)}</CreationDate>
                    <StyledDivider />
                    {children}
                </StyledList>
            )}
        />
    );
};
